// @ts-strict-ignore
import { lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { BuildingBlockArticleManager } from './building-block-article.manager';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { Connection } from '@3d-scene/model3d/connection';
import { GroupedCubesSearchRequirements } from '../../../model/vo/grouped-cubes-search-requirements';
import { PropertyCreator } from '@article/util/property-creator';
import { SuitableConnectionsVO } from '../model/vo/suitable-connections.vo';
import { UndoRedoManager } from '@shared/undo/undo-redo.manager';
import { ValidationService } from '../../../manager/validation.service';
import { ArticleUtils } from '@article/util/article-utils';
import { MountingRailHelper } from '../../../helper/mounting-rail.helper';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { BridgesConnectorHelper } from '@article/helper/bridges-connector.helper';
import { IEKBuildingBlockVO } from '../../../model/vo/building-block.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUsagePlausiPropertyDef } from '@article/constants/article-usage-plausi-property-def';
import { ArticleInfoConnectionSetVO } from '../model/vo/accessory-search-result.vo';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleVO } from '@article/model/vo/article.vo';
import { Injectable } from '@angular/core';
import { Log } from '@shared/debug/log';
import { QuickFixManager } from '@shared/manager/quick-fix.manager';
import { BuildingBlockInfo } from './building-block-info';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ArticleCreationManagerFactory } from './article-creation.manager.factory';
import { ArticleCreationManager } from './article-creation.manager';
import { ConnectionCubeSearchVector } from '@article/components/connections/connection-creation/search/connection-cube-search-vector';
import { AccessoryService } from '@article/service/accessory.service';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { EShopDataService } from '../../../service/eshop-data.service';
import { MountingAssistantService } from '@configuration/assistants/mounting/mounting-assistant.service';
import { CreateProjectService } from '../../../service/create-project.service';
import { OverlayService } from '@shared/overlay/overlay.service';
import { OverlayBackground } from '@shared/overlay/overlay.type';

export enum BuildingBlockTypes {
  IEK_SUPPLY = 'IEKSupply',
  IEK_CIRCUIT_START = 'IEKBuildingCircuitStart',
  IEK_CIRCUIT_END = 'IEKBuildingCircuitEnd',
  IEK_SUBCIRCUIT = 'IEKSubcircuit',
}

@Injectable({ providedIn: 'root' })
export class IEKAssistantManager {
  private static readonly circuitBuildingBlockTypes: string[] = [
    BuildingBlockTypes.IEK_CIRCUIT_START,
    BuildingBlockTypes.IEK_CIRCUIT_END,
    BuildingBlockTypes.IEK_SUBCIRCUIT,
  ];

  private _buildingBlocksArticleUsageIds: Map<IEKBuildingBlockVO, string[]> = new Map<IEKBuildingBlockVO, string[]>();

  constructor(
    private alertHelper: AlertHelper,
    private buildingBlockArticleManager: BuildingBlockArticleManager,
    private eShopDataService: EShopDataService,
    private validationService: ValidationService,
    private undoRedoManager: UndoRedoManager,
    private mountingRailHelper: MountingRailHelper,
    private bridgesConnectorHelper: BridgesConnectorHelper,
    private propertyCreator: PropertyCreator,
    private containerService: ContainerService,
    private articleContainerUtils: ArticleContainerUtils,
    private quickFixManager: QuickFixManager,
    private buildingBlockInfo: BuildingBlockInfo,
    private articleManager: ArticleManager,
    private articleCreationManagerFactory: ArticleCreationManagerFactory,
    private accessoryService: AccessoryService,
    private mountingAssistantService: MountingAssistantService,
    private createProjectService: CreateProjectService,
    private overlayService: OverlayService,
  ) {
  }

  public async createAssistantsProject(guid: string) {
    if (await this.createProjectService.startNewProject()) {
      await this.overlayService.doWithOverlay(async () => {

        await this.createProjectService.createFreeMounting();

        let createdUsageIds: string[] = null;
        const buildingBlocksToProcess = await this.loadBuildingBlocks(guid);
        for (const buildingBlock of buildingBlocksToProcess) {
          if (createdUsageIds) {
            this.buildingBlockAdded(buildingBlock, createdUsageIds);
          }
          await this.buildingBlockArticleManager.addBuildingBlockToProject(buildingBlock.name, false);
          createdUsageIds = this.buildingBlockInfo.createdArticleUsageIds;
        }

        await this.setLastTerminalInCircuitProperties();
        this.projectBuildFinished();

        this.mountingAssistantService.start('iek');
      }, OverlayBackground.color);
    }
  }

  private async loadBuildingBlocks(guid: string) {
    try {
      return await lastValueFrom(this.eShopDataService.loadBuildingBlocksByEShopStorageId(guid));
    } catch (error) {
      await this.alertHelper.showError(
        'iekAssistant.title.creating_project_failed_due_to_invalid_link',
        'iekAssistant.message.creating_project_failed_due_to_invalid_link',
      );
      return [];
    }
  }

  private async setLastTerminalInCircuitProperties(): Promise<void> {
    this._buildingBlocksArticleUsageIds.forEach((articleUsageIds: string[], buildingBlock: IEKBuildingBlockVO) => {
      if (buildingBlock.useSeparationPlate && ArrayUtils.isNotEmpty(articleUsageIds)) {
        const lastTerminal: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsageIds[articleUsageIds.length - 1]);
        if (lastTerminal && !ArticleUtils.isEndPlateArticle(lastTerminal.article)) {
          ArticleUsageUtils.setArticleUsagePlausiPropertyBooleanValue(
            lastTerminal.articleUsage,
            ArticleUsagePlausiPropertyDef.LAST_TERMINAL_IN_CIRCUIT,
            true
          );
        }
      }
    });
    await this.validateProject();
  }

  /* Project validation  */

  private async validateProject(): Promise<void> {
    try {
      const validationResult = await this.validationService.validateProjectAsync();
      await this.quickFixManager.projectValidatedSuccessHandler(validationResult);
      await this.processAddNbusbars();
    } catch (error) {
      Log.error('Could not validate project: ', error)
    }
  }

  /* Adding N-Busbar accessories */

  private async processAddNbusbars(): Promise<void> {
    const allArticleContainers: ArticleContainer[] = this.containerService.enumerateArticleContainersInGlobal();
    let busbarCarrierArticles: ArticleContainer[] = allArticleContainers.filter(a => this.articleContainerUtils.isBusbarCarrierArticle(a));
    busbarCarrierArticles = this.mountingRailHelper.getInsertArticleHelper().sortArticles(busbarCarrierArticles);
    await this.connectAccessoryToArticles(busbarCarrierArticles);
  }

  private async connectAccessoryToArticles(articles: ArticleContainer[]): Promise<void> {
    if (articles.length < 2) {
      await this.addingNbusbarsFinished();
      return;
    }

    const firstArticle: ArticleContainer = articles.shift();
    const secondArticle: ArticleContainer = articles.shift();

    const firstArticleBusbarConnections: Connection[] = ConnectionInfoUtils.getFreeBusbarConnections(firstArticle);
    const secondArticleBusbarConnections: Connection[] = ConnectionInfoUtils.getFreeBusbarConnections(secondArticle);

    if (!firstArticleBusbarConnections.length || !secondArticleBusbarConnections.length) {
      await this.connectAccessoryToArticles(articles);
      return;
    }

    const articleCreationManager = this.articleCreationManagerFactory.getNewInstance();
    articleCreationManager.showFaultMessage = false;
    articleCreationManager.createUndoOperation = false;
    articleCreationManager.detachArticleUsageIds = null;
    articleCreationManager.replaceArticleUsageIds = [];

    const busbarConnections = [...firstArticleBusbarConnections, ...secondArticleBusbarConnections];
    const groupedCubesSearchRequirements = [
      new GroupedCubesSearchRequirements(this.propertyCreator.createCubesSearchRequirementsByDefault(busbarConnections)),
    ];
    this.accessoryService
      .searchAccessoriesByGroupWithoutPrintLabeling(groupedCubesSearchRequirements, [ArticleTypeConstants.TYPE_N_BUSBAR])
      .subscribe(accessorySearchResult => this.accessoryListLoaded(accessorySearchResult.vo.articleInfoConnectionSet, articleCreationManager, articles, firstArticle, secondArticle, accessorySearchResult.connectionCubeVectors))
  }

  private async accessoryListLoaded(accessoryList: ArticleInfoConnectionSetVO[],
                                    articleCreationManager: ArticleCreationManager,
                                    processedArticles: ArticleContainer[],
                                    firstArticle: ArticleContainer,
                                    secondArticle: ArticleContainer,
                                    connectionCubeVectors: ConnectionCubeSearchVector[]): Promise<void> {
    const accToConnect: ArticleInfoConnectionSetVO = accessoryList.shift();
    await this.connectArticleBySuitableConnections(articleCreationManager, processedArticles, firstArticle, secondArticle, accessoryList, accToConnect, connectionCubeVectors);
  }

  private async onArticlesAdded(articleCreationManager: ArticleCreationManager,
                                processedArticles: ArticleContainer[],
                                firstArticle: ArticleContainer,
                                secondArticle: ArticleContainer,
                                accessoryList: ArticleInfoConnectionSetVO[],
                                connectionCubeVectors: ConnectionCubeSearchVector[]): Promise<void> {
    const isConnectAccessoryAvailable = ConnectionInfoUtils.getFreeBusbarConnections(firstArticle, false).length
    && ConnectionInfoUtils.getFreeBusbarConnections(secondArticle, false).length;
    if (!accessoryList.length || !isConnectAccessoryAvailable) {
      articleCreationManager.clean();
      await this.connectAccessoryToArticles(processedArticles);
      return;
    }
    const accToConnect: ArticleInfoConnectionSetVO = accessoryList.shift();
    await this.connectArticleBySuitableConnections(articleCreationManager, processedArticles, firstArticle, secondArticle, accessoryList, accToConnect, connectionCubeVectors);
  }

  private async connectArticleBySuitableConnections(articleCreationManager: ArticleCreationManager,
                                                    processedArticles: ArticleContainer[],
                                                    firstArticle: ArticleContainer,
                                                    secondArticle: ArticleContainer,
                                                    accessoryList: ArticleInfoConnectionSetVO[],
                                                    articleInfoConnection: ArticleInfoConnectionSetVO,
                                                    connectionCubeVectors: ConnectionCubeSearchVector[]): Promise<void> {
    if (ArticleUtils.isArticleOutdated(articleInfoConnection.articleWithTechnicalProperties)) {
          return;
      }
    const suitableConnections = await this.articleManager.loadFullArticle(articleInfoConnection.articleWithTechnicalProperties.articleId).pipe(
          map((article: ArticleVO) => {
              const suitableConnections: SuitableConnectionsVO = new SuitableConnectionsVO();
              suitableConnections.connectionSets = articleInfoConnection.connectionSets;
              suitableConnections.article = article;
              return suitableConnections;
          })).toPromise();
    await articleCreationManager.createArticlesBySuitableConnections(suitableConnections, connectionCubeVectors);
    await this.onArticlesAdded(articleCreationManager, processedArticles, firstArticle, secondArticle, accessoryList, connectionCubeVectors);
  }

  private async addingNbusbarsFinished(): Promise<void> {
    try {
      await this.connectPENBridges();
      await this.connectBridges();
    } catch (error) {
      Log.error('Could not connect pen bridges: ', error);
    }
  }

  private connectPENBridges(): Promise<void> {
    if (this.shouldAddPENBridges()) {
      const articleUsageIdsToExclude: string[] = this.getCreatedArticleUsageIdsByBuildingBlockTypes(IEKAssistantManager.circuitBuildingBlockTypes);
      return this.bridgesConnectorHelper.connectBridges(articleUsageIdsToExclude, true, false);
    }
  }

  private shouldAddPENBridges(): boolean {
    for (const buildingBlock of Array.from(this._buildingBlocksArticleUsageIds.keys())) {
      if (buildingBlock.type === BuildingBlockTypes.IEK_SUPPLY && buildingBlock.usePENBridge) {
        return true;
      }
    }
    return false;
  }

  private getCreatedArticleUsageIdsByBuildingBlockTypes(types: string[]): string[] {
    const createdArticleUsageIds: string[] = [];
    this._buildingBlocksArticleUsageIds.forEach((articleUsageIds: string[], buildingBlock: IEKBuildingBlockVO) => {
      if (types.includes(buildingBlock.type)) {
        createdArticleUsageIds.push(...articleUsageIds);
      }
    });
    return createdArticleUsageIds;
  }

  private async connectBridges(): Promise<void> {
    const articleUsageIdsToExclude: string[] = this.getCreatedArticleUsageIdsByBuildingBlockTypes([BuildingBlockTypes.IEK_SUPPLY]);
    try {
      await this.bridgesConnectorHelper.connectBridges(articleUsageIdsToExclude, false, false);
      this.projectBuildFinished();
    } catch (error) {
      Log.error('Could not connect bridges: ', error);
    }
  }

  private projectBuildFinished(): void {
    this._buildingBlocksArticleUsageIds.clear();
    this.removeLastTerminalInCircuitProperties();
    this.undoRedoManager.clearAll();
  }

  private removeLastTerminalInCircuitProperties(): void {
    const articleContainers: ArticleContainer[] = this.containerService.enumerateArticleContainersInGlobal();
    articleContainers.forEach((articleContainer) => {
      ArticleUsageUtils.removeArticleUsagePlausiProperty(articleContainer.articleUsage, ArticleUsagePlausiPropertyDef.LAST_TERMINAL_IN_CIRCUIT);
    });
  }

  public buildingBlockAdded(buildingBlock: IEKBuildingBlockVO, createdUsageIds: string[]): void {
    if (this.buildingBlockInfo.dispatchBuildingBlockAttached) {
      this.buildingBlockInfo.dispatchBuildingBlockAttached = false;
      this._buildingBlocksArticleUsageIds.set(buildingBlock, createdUsageIds);
    }
  }
}
